import { GridApi } from 'ag-grid-community';
import { useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { REDUX_SLICE_KEYS, ROLES } from 'common/constants';
import { ErrorToastValidation } from 'components';
import { DropdownOption } from 'components/Dropdown/types';
import { setErrorMessages } from 'components/ErrorMessageHandler/slice';
import { IntlType } from 'components/ErrorToastValidation/types';
import { getPaymentDateByAuthorizePayment } from 'components/PaymentDatePicker/slice';
import { getCompanyId } from 'components/StoreSelect/slice';
import { useSubmitAuthorizedPayment } from 'hooks';
import {
  useFetchCashInBankInterCompany,
  useGlValidation,
  useUpdateInvoiceDescriptions
} from 'hooks/useClientDataServices';
import { getUserRole } from 'pages/LandingPage/slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { formatLocaleDate } from 'utils/dateUtils';
import { formatCurrency } from 'utils/formatCurrency';
import { handleJsonError } from 'utils/handleJsonError';
import { InvoiceTable } from '../components';
import { AuthorizePaymentHeader } from '../components/AuthorizePaymentHeader/AuthorizePaymentHeader';
import { messages, errors } from '../messages';
import { getAuthorizeTotal, getGridApi } from '../selector';
import {
  getUpdateInvoiceDescriptionsPayload,
  setAuthorizePaymentData
} from '../slice';
import { StyledTotal, StyledTotalAmounts, StyledSpan } from '../styled';
import { InterCompanyAuthorizationPaymentRecordType } from '../types';
import { getFormattedIntlErrorList, getFormattedList } from '../utils';
import { getStoreIdByCompanyId } from './helpers';
import { InterCompanyFields } from './InterCompanyFields';
import { IntercompanyJournals } from './types';

export const InterCompany = (): React.JSX.Element => {
  const intl: IntlShape = useIntl();
  const dispatch = useAppDispatch();
  const authorizePaymentDate = useAppSelector(getPaymentDateByAuthorizePayment);
  const totalAmount = useAppSelector(getAuthorizeTotal);
  const companyId = useAppSelector(getCompanyId);
  const userRole = useAppSelector(getUserRole);
  const gridApi: GridApi | null = useAppSelector(getGridApi);
  const updateInvoiceDescriptionsPayload = useAppSelector(
    getUpdateInvoiceDescriptionsPayload
  );
  const [selectedJournal, setSelectedJournal] = useState<DropdownOption>();
  const [postingDescription, setPostingDesc] = useState<string>('');
  const [errorToast, setErrorToast] = useState<boolean>(false);
  const [validationErrorList, setValidationErrorList] = useState<IntlType[]>();
  const [selectedCashCompany, setSelectedCashCompany] =
    useState<DropdownOption>();
  const [selectedCashInBank, setSelectedCashInBank] =
    useState<DropdownOption>();

  const { data } = useFetchCashInBankInterCompany(false);

  const {
    mutate: updateInvoiceDescriptions,
    isLoading: isUpdateInvoiceDescriptionsLoading
  } = useUpdateInvoiceDescriptions();

  const { mutate: authorizeReviewedData, isLoading } =
    useSubmitAuthorizedPayment();

  const { mutate: glValidationData, isLoading: isLoadingValidation } =
    useGlValidation();

  const validateDesc = (desc: string) => {
    const isInValid = desc === '';
    return !isInValid;
  };

  const handleAuthorizePayment = () => {
    const authorizePaymentList: InterCompanyAuthorizationPaymentRecordType[] =
      [];
    gridApi?.forEachNode(node => authorizePaymentList.push(node.data));
    const validPostingDesc = validateDesc(postingDescription);
    const cashInBankAccountNumber = selectedCashInBank?.label.split(' ')[0];
    const cashCompanyId = selectedCashCompany?.value;
    const cashCompanyStoreid = getStoreIdByCompanyId(
      data?.cashCompanies,
      cashCompanyId
    );

    const journalSelectedObj = handleJsonError(
      selectedJournal?.value ?? ''
    ) as IntercompanyJournals;

    if (
      validPostingDesc &&
      cashCompanyStoreid !== '' &&
      cashInBankAccountNumber !== '' &&
      journalSelectedObj.journalNumber !== '' &&
      cashInBankAccountNumber !== undefined
    ) {
      const authorizePaymentReview = {
        paymentDate: authorizePaymentDate,
        accountingDate: authorizePaymentDate,
        cashCompanyId,
        cashInBankStoreId: cashCompanyStoreid,
        journalNumber: journalSelectedObj?.journalNumber,
        journalCompanyId: journalSelectedObj?.companyId,
        journalStoreId: journalSelectedObj?.storeId,
        companyId,
        cashInBankAccount: cashInBankAccountNumber,
        postingDescription,
        authorizationPaymentList: getFormattedList(authorizePaymentList)
      };
      dispatch(setAuthorizePaymentData(authorizePaymentReview));
      glValidationData(authorizePaymentReview, {
        onSuccess: glPostingValidationResponse => {
          if (glPostingValidationResponse?.errorList?.length === 0) {
            authorizeReviewedData(authorizePaymentReview);
          } else if (glPostingValidationResponse?.errorList?.length > 0) {
            setValidationErrorList(
              getFormattedIntlErrorList(glPostingValidationResponse?.errorList)
            );
            setErrorToast(true);
          }
        },
        onError: () => {
          dispatch(
            setErrorMessages([
              intl.formatMessage(errors.glPostingValidationFailure)
            ])
          );
        }
      });
      if (updateInvoiceDescriptionsPayload.length > 0) {
        updateInvoiceDescriptions(updateInvoiceDescriptionsPayload);
      }
    }
  };

  return (
    <>
      <AuthorizePaymentHeader
        handleAuthorizePayment={handleAuthorizePayment}
        disableAuthButton={userRole === ROLES.AP_CLERK}
        isLoading={
          isLoading || isUpdateInvoiceDescriptionsLoading || isLoadingValidation
        }
      />
      {errorToast && (
        <ErrorToastValidation
          errorList={validationErrorList}
          handleDismiss={() => setErrorToast(false)}
          onlyOneButtonProps
        />
      )}
      <StyledTotalAmounts>
        <StyledTotal>{intl.formatMessage(messages.total)}</StyledTotal>
        <StyledSpan>{formatCurrency(totalAmount)}</StyledSpan>
      </StyledTotalAmounts>
      <InterCompanyFields
        whichScreen={REDUX_SLICE_KEYS.authorizeReview}
        setSelectedCashCompany={setSelectedCashCompany}
        setSelectedCashInBank={setSelectedCashInBank}
        setSelectedJournal={setSelectedJournal}
        setPostingDesc={setPostingDesc}
        selectedCashCompany={selectedCashCompany}
        postingDescription={postingDescription}
      />
      <InvoiceTable
        paymentDate={formatLocaleDate(authorizePaymentDate)}
        selectedCashCompany={selectedCashCompany}
        selectedCashInBank={selectedCashInBank}
        selectedJournal={selectedJournal}
        postingDescription={postingDescription}
      />
    </>
  );
};
